import {
  FETCH_GLOBAL_ENABLE_PWDLIST,
  FETCH_GLOBAL_PWDLIST,
  LOAD_ENABLE_PWD,
  LOAD_PWDLIST,
} from 'components/DeviceCredentials/constants';
import {
  fetchEnablePwdList,
  fetchPwdList,
} from 'components/DeviceCredentials/sagas';
import {
  JOB_ACTIONS,
  JOB_ADDED,
  JOB_QUEUED,
  JOB_STARTED,
  JOB_STATUSES,
  JOB_UPDATED,
} from 'containers/App/Header/JobManagement/constants';
import {
  getJobs,
  jobStopSaga,
} from 'containers/App/Header/JobManagement/sagas';
import { take, takeEvery } from 'containers/ErrorBoundary';
import { all, call, put, select } from 'redux-saga/effects';
import { displayMessage } from 'utils/request/displayMessage';
import { requestForSaga } from 'utils/request/request';
import { requestCustomErrorMessage } from 'utils/request/requestCustomErrorMessage';
import { columns as inventoryColumns } from './Tabs/Inventory';
import {
  changeSnapshot,
  clearTabs,
  disableStartBtn,
  enableStartBtn,
  saveInvalidPasswords,
  showAddLoader,
  showRefreshLoader,
} from './actions';
import {
  BANDWIDTH_MSG_RECEIVED,
  CHANGE_SNAPSHOT,
  CLEAR_DEVICES,
  CLONE_SNAPSHOT,
  CLOSE_SETTINGS_NO_PASSWORDS,
  DELETE_DEVICE,
  DELETE_DEVICE_FINISH,
  DELETE_DEVICE_STOP,
  DELETE_SNAPSHOT,
  DELETE_SNAPSHOT_FINISH,
  DISCOVERY_PROCESS_START,
  DISCOVERY_PROCESS_STOP,
  DOWNLOAD_LOG,
  DOWNLOAD_SNAPSHOT,
  FETCH_DEVICES,
  FETCH_DISABLED_POST_DISCOVERY_ACTIONS,
  FETCH_DISCOVERY_INIT,
  FETCH_DISCOVERY_TASKS,
  FETCH_LOGS,
  FETCH_SITE_SEPARATION,
  FETCH_SNAPSHOT_DATA,
  FETCH_SNAPSHOT_MANAGEMENT,
  LOADING_STARTED,
  LOAD_SNAPSHOT,
  LOAD_SNAPSHOT_FINISH,
  LOCK_SNAPSHOT,
  NAME_CHANGE,
  NETWORKER_LOGS_RECEIVED,
  NEW_DEVICE_SAVED,
  NOTE_CHANGE,
  OPEN_NEW_SETTINGS,
  OPEN_SETTINGS,
  PAGE_URL,
  RUN_TOPOLOGY_CALCULATION,
  SAVE_DEVICES,
  SERVER_TS_RECEIVED,
  SETTINGS_NO_PASSWORD_DIALOGUE_NAME,
  SET_SHAPER_SETTINGS,
  SNAPSHOT_ID_LS_KEY,
  SNAPSHOT_INFO_RECEIVED,
  SNAPSHOT_PREVENT_DEVICE_LOAD,
  SNAPSHOT_STATUS_RECEIVED,
  TASKER_LOGS_RECEIVED,
  UNLOAD_SNAPSHOT,
  UNLOAD_SNAPSHOT_FINISH,
  UNLOCK_SNAPSHOT,
  UPLOAD_SNAPSHOT,
  UPLOAD_SNAPSHOT_FINISH,
  WORKER_LAST_COMMUNICATION,
  WORKER_LOGS_RECEIVED,
} from './constants';
import {
  isCurrentSnapshotRunning,
  isSnapshotLoaded,
  isSnapshotManagementPage,
  isSnapshotRunning,
  userInSnapshotsManagement,
} from './helpers';

import { isNonNullable, isNullable } from '@ipfabric/utils';
import { LicenseLimitChecker } from '@ipfabric/utils/license/licenseLimitChecker';
import {
  FETCH_HISTORY_SEEDS,
  FETCH_IPLIST,
} from 'components/DiscoverySeed/constants';
import { fetchIpListSaga } from 'components/DiscoverySeed/sagas';
import { toggleConfirmDialog } from 'containers/ConfirmDialog/actions';
import { selectLanguage } from 'containers/LanguageProvider/selectors';
import { showSuccess } from 'containers/Toaster/actions';
import { DISCOVERY_WIZARD_FINAL_STEP_PATH } from 'features/DiscoverySettingsForms/composition/DiscoveryConfigWizard/wizardConfig';
import FileSaver from 'file-saver';
import { translationMessages } from 'i18n';
import get from 'lodash/get';
import { assoc, pipe } from 'ramda';
import { push } from 'redux-first-history';

const LOG_REGEX = /(info|warn|error): \[.+]/;

export function* getSelectedSnapshot() {
  return yield select((s) =>
    s.getIn(['snapshotManagement', 'selectedSnapshot']),
  );
}

export function* getSnapshots() {
  return yield select((s) => s.getIn(['snapshotManagement', 'snapshots']));
}

function* updateSelectedSnapshot() {
  const selectedSnapshot = yield getSelectedSnapshot();
  const allSnapshots = yield getSnapshots();
  const onlyLoadedSnapshots = allSnapshots.filter((item) =>
    isSnapshotLoaded(item.get('state')),
  );

  const isLoaded = onlyLoadedSnapshots.find(
    (item) => item.get('id') === selectedSnapshot,
  );
  const savedSnapshotId = localStorage.getItem(SNAPSHOT_ID_LS_KEY);
  if (isLoaded) {
    return;
  }

  const snapshot =
    onlyLoadedSnapshots.find((item) => item.get('id') === savedSnapshotId) ||
    onlyLoadedSnapshots.find((item) => !isCurrentSnapshotRunning(item)) ||
    allSnapshots.find((item) => isSnapshotLoaded(item.get('state')));

  yield put(changeSnapshot(snapshot ? snapshot.get('id') : null));
}

function* fetchShaper() {
  if (userInSnapshotsManagement()) {
    const shaper = yield call(requestForSaga, { url: '/discovery/shaper' });
    yield put({
      type: `${SET_SHAPER_SETTINGS}_SUCCESS`,
      payload: shaper,
    });
  }
}

/**
 * Prevent application from logging out on app refresh when user doesn't have correct roles
 */
function* fetchShaperWithoutRole() {
  try {
    yield call(fetchShaper);
  } catch (e) {
    if (e.type !== 'INSUFFICIENT_RIGHTS') {
      throw e;
    }
  }
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy code
export function* fetchSnapshotsSaga(action) {
  try {
    const [data] = yield all([
      call(requestForSaga, { url: '/snapshots' }),
      call(fetchShaperWithoutRole),
    ]);
    const jobs = yield getJobs();
    const dataWithLoading = data.map((item) => {
      const foundJob = jobs.find(
        (job) =>
          job.get('snapshot') === item.id &&
          job.get('action') === JOB_ACTIONS.snapshotLoad &&
          job.get('status') === JOB_STATUSES.running,
      );
      if (foundJob) {
        return assoc('loading', true, item);
      }
      return item;
    });
    yield put({
      type: `${FETCH_SNAPSHOT_MANAGEMENT}_SUCCESS`,
      payload: { data: dataWithLoading },
    });

    const selectSnapshotKey = get(action, 'payload.selectSnapshotKey', '');
    const snapshotId = get(action, 'payload.params.snapshotId', '').split(',');
    if (
      selectSnapshotKey &&
      (window.location.pathname.startsWith(`${PAGE_URL}/new`) ||
        window.location.pathname === DISCOVERY_WIZARD_FINAL_STEP_PATH)
    ) {
      const preventDeviceLoad = get(action, 'payload.preventDeviceLoad');
      if (preventDeviceLoad) {
        const snapshots = yield call(getSnapshots);
        const foundIndex = snapshots.findIndex(
          (snapshot) => snapshot.get('id') === selectSnapshotKey,
        );
        if (foundIndex > -1) {
          yield put({
            type: SNAPSHOT_PREVENT_DEVICE_LOAD,
            payload: {
              index: foundIndex,
            },
          });
        }
      }
      yield put(changeSnapshot(selectSnapshotKey));
      const finishStatus = get(action, 'payload.finishStatus');
      if (finishStatus !== 'done') {
        yield put(clearTabs());
      }
    } else if (snapshotId.length === 1 && snapshotId[0]) {
      const foundSnapshot = dataWithLoading.find(
        (snapshot) =>
          snapshot.id === snapshotId[0] && snapshot.state === 'loaded',
      );
      if (foundSnapshot) {
        yield put(changeSnapshot(snapshotId[0]));
      }
    } else {
      yield updateSelectedSnapshot();
    }
    const enableStartButton = get(action, 'payload.enableStartBtn');
    if (enableStartButton === true) {
      yield put(enableStartBtn());
    }
  } catch (e) {
    yield put({ type: `${FETCH_SNAPSHOT_MANAGEMENT}_ERROR` });
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* requestLog(service, selectedSnapshot) {
  try {
    const result = yield call(requestForSaga, {
      url: `/os/service-log/${service}/${selectedSnapshot}`,
      method: 'GET',
    });
    return result.split('\n').reduce((acc, line) => {
      try {
        if (!line) {
          return acc;
        }
        if (LOG_REGEX.test(line)) {
          return acc.concat(line);
        }
        return acc;
      } catch {
        return acc;
      }
    }, []);
  } catch (e) {
    if (get(e, 'code') === 'API_NOT_FOUND') {
      return [];
    }
    throw e;
  }
}

function* fetchLogsSaga() {
  try {
    if (userInSnapshotsManagement()) {
      const selectedSnapshot = yield getSelectedSnapshot();
      if (selectedSnapshot) {
        yield put({ type: `${FETCH_LOGS}_START` });
        const [worker, netWorker, tasker] = yield all([
          call(requestLog, 'worker', selectedSnapshot),
          call(requestLog, 'networker', selectedSnapshot),
          call(requestLog, 'tasker', selectedSnapshot),
        ]);
        yield put({
          type: `${FETCH_LOGS}_SUCCESS`,
          payload: {
            worker,
            netWorker,
            tasker,
          },
        });
      }
    }
  } catch (e) {
    yield put({ type: `${FETCH_LOGS}_ERROR` });
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* loadSnapshotsSaga(action) {
  const { data } = action.payload;
  const idList = data.map((item) => item.id);
  try {
    yield put({ type: `${LOAD_SNAPSHOT}_START`, payload: { idList } });
    yield call(requestForSaga, {
      url: '/snapshots/load',
      method: 'POST',
      data: data.map(({ id, tsStart, name }) => ({
        jobDetail: name || tsStart,
        id,
      })),
    });
    yield call(
      displayMessage,
      'info',
      'snapshotManagement.request.snapshotLoadStarted',
    );
    yield put({ type: `${LOAD_SNAPSHOT}_SUCCESS`, payload: { idList } });
  } catch (e) {
    yield put({ type: `${LOAD_SNAPSHOT}_ERROR`, payload: { idList } });
    if (
      ['API_SNAPSHOT_MAX_LOADED_REACHED', 'API_NOT_FOUND'].includes(
        get(e, 'code'),
      )
    ) {
      yield call(requestCustomErrorMessage, 'warning', e);
      return;
    }
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* unloadSnapshotsSaga(action) {
  const { data } = action.payload;
  const idList = data.map((item) => item.id);
  try {
    yield put({ type: `${UNLOAD_SNAPSHOT}_START`, payload: { id: idList } });
    yield call(requestForSaga, {
      url: '/snapshots/unload',
      method: 'POST',
      data: data.map(({ id, tsStart, name }) => ({
        jobDetail: name || tsStart,
        id,
      })),
    });
    yield put({ type: `${UNLOAD_SNAPSHOT}_SUCCESS`, payload: { id: idList } });
  } catch (e) {
    yield put({ type: `${UNLOAD_SNAPSHOT}_ERROR`, payload: { id: idList } });
    if (['API_SNAPSHOT_LOCKED', 'API_NOT_FOUND'].includes(get(e, 'code'))) {
      yield call(requestCustomErrorMessage, 'warning', e);
      return;
    }
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* deleteSnapshotsSaga(action) {
  const { data } = action.payload;
  const idList = data.map((item) => item.id);
  try {
    yield put({ type: `${DELETE_SNAPSHOT}_START`, payload: { id: idList } });
    yield call(requestForSaga, {
      url: '/snapshots',
      method: 'DELETE',
      data: data.map(({ id, tsStart, name }) => ({
        jobDetail: name || tsStart,
        id,
      })),
    });
    yield put({ type: `${DELETE_SNAPSHOT}_SUCCESS`, payload: { id: idList } });
  } catch (e) {
    yield put({ type: `${DELETE_SNAPSHOT}_ERROR`, payload: { id: idList } });
    if (['API_SNAPSHOT_LOCKED', 'API_NOT_FOUND'].includes(get(e, 'code'))) {
      yield call(requestCustomErrorMessage, 'warning', e);
      return;
    }
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

export function* downloadSnapshotSaga(action) {
  const { id } = action.payload;
  try {
    yield call(requestForSaga, {
      url: `/snapshots/${id}/download`,
      method: 'GET',
    });
  } catch (e) {
    if (['API_NOT_FOUND'].includes(get(e, 'code'))) {
      yield call(requestCustomErrorMessage, 'warning', e);
      return;
    }
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

export function* runTopologyCalculationSaga(action) {
  const { id } = action.payload;
  try {
    yield call(requestForSaga, {
      url: `/snapshots/${id}/run-assurance-engine`,
      method: 'POST',
    });
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* selectUploadedSnapshot(action, snapshotId) {
  yield call(fetchSnapshotsSaga);
  const { currentSnapshotId } = action.payload;
  if (isNonNullable(snapshotId) && snapshotId !== currentSnapshotId) {
    const content = document.querySelector('#snapshot-management-content');
    if (content) {
      yield put(push(`${PAGE_URL}/${snapshotId}`));
      if (isNullable(currentSnapshotId)) {
        content.classList.add('showcontent');
        content.classList.remove('hidecontent');
      }
    }
  }
}

export function* uploadSnapshotSaga(action) {
  try {
    yield put({ type: `${UPLOAD_SNAPSHOT}_START` });
    const formData = new FormData();
    formData.append('file', action.payload.file);
    const { snapshot } = yield call(requestForSaga, {
      url: '/snapshots/upload',
      method: 'POST',
      formData: true,
      data: formData,
    });
    yield put({ type: `${UPLOAD_SNAPSHOT}_SUCCESS` });
    yield selectUploadedSnapshot(action, snapshot);
  } catch (e) {
    yield put({ type: `${UPLOAD_SNAPSHOT}_ERROR` });
    if (get(e, 'code') === 'API_SNAPSHOT_CONFLICT') {
      yield call(
        displayMessage,
        'warning',
        'snapshotManagement.request.snapshotConflict',
      );
      const snapshotId = get(e, 'payload.response.data.snapshot');
      yield selectUploadedSnapshot(action, snapshotId);
    } else {
      yield call(requestCustomErrorMessage, 'danger', e);
      throw e;
    }
  }
}

const getSuccessMessage = (type) => {
  switch (type) {
    case DELETE_SNAPSHOT_FINISH:
      return 'delete';
    case UNLOAD_SNAPSHOT_FINISH:
      return 'unload';
    default:
      return 'success';
  }
};

function* realodDataSaga(action) {
  yield call(fetchSnapshotsSaga);
  yield put({ type: `${action.type}_SUCCESS`, payload: action.payload });
  if (
    action.type === DELETE_SNAPSHOT_FINISH ||
    action.type === UNLOAD_SNAPSHOT_FINISH
  ) {
    yield call(
      displayMessage,
      'success',
      `snapshotManagement.request.${getSuccessMessage(action.type)}`,
    );
  }
}

function* nameChangeSaga(action) {
  try {
    yield put({ type: `${NAME_CHANGE}_START`, payload: action.payload });
    yield call(requestForSaga, {
      url: `/snapshots/${get(action, 'payload.id')}`,
      method: 'PATCH',
      data: { name: get(action, 'payload.name') },
    });
    yield put({ type: `${NAME_CHANGE}_SUCCESS`, payload: action.payload });
  } catch (e) {
    yield put({ type: `${NAME_CHANGE}_ERROR`, payload: action.payload });
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* noteChangeSaga(action) {
  try {
    yield put({ type: `${NOTE_CHANGE}_START`, payload: action.payload });
    yield call(requestForSaga, {
      url: `/snapshots/${get(action, 'payload.id')}`,
      method: 'PATCH',
      data: { note: get(action, 'payload.note') },
    });
    yield put({ type: `${NOTE_CHANGE}_SUCCESS`, payload: action.payload });
  } catch (e) {
    yield put({ type: `${NOTE_CHANGE}_ERROR`, payload: action.payload });
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* lockSnapshotSaga(action) {
  try {
    yield put({ type: `${LOCK_SNAPSHOT}_START`, payload: action.payload });
    yield call(requestForSaga, {
      url: `/snapshots/${get(action, 'payload.id')}/lock`,
      method: 'POST',
    });
    yield put({ type: `${LOCK_SNAPSHOT}_SUCCESS`, payload: action.payload });
  } catch (e) {
    yield put({ type: `${LOCK_SNAPSHOT}_ERROR`, payload: action.payload });
    if (
      ['API_SNAPSHOT_MAX_LOCKED_REACHED', 'API_NOT_FOUND'].includes(
        get(e, 'code'),
      )
    ) {
      yield call(requestCustomErrorMessage, 'warning', e);
      return;
    }
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* unlockSnapshotSaga(action) {
  try {
    yield put({ type: `${UNLOCK_SNAPSHOT}_START`, payload: action.payload });
    yield call(requestForSaga, {
      url: `/snapshots/${get(action, 'payload.id')}/unlock`,
      method: 'POST',
    });
    yield put({ type: `${UNLOCK_SNAPSHOT}_SUCCESS`, payload: action.payload });
  } catch (e) {
    yield put({ type: `${UNLOCK_SNAPSHOT}_ERROR`, payload: action.payload });
    if (['API_NOT_FOUND'].includes(get(e, 'code'))) {
      yield call(requestCustomErrorMessage, 'warning', e);
      return;
    }
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

// Refresh snapshot with site for selectedSnapshot
function* fetchSnapshotDataSaga(snapshotId) {
  const selectedSnapshot = yield getSelectedSnapshot();
  if (!selectedSnapshot) {
    return;
  }
  const snapshot = yield call(requestForSaga, {
    url: `/snapshots/${selectedSnapshot}`,
    method: 'GET',
  });
  yield put({
    type: `${FETCH_SNAPSHOT_DATA}_SUCCESS`,
    payload: isNonNullable(snapshotId)
      ? assoc('snapshotId', snapshotId, { snapshot })
      : { snapshot },
  });
}

function* fetchDevices(action) {
  const loader = get(action, 'payload.loader', false);
  const selectedSnapshot = yield getSelectedSnapshot();
  try {
    if (loader) {
      yield put({
        type: `${SAVE_DEVICES}_START`,
        payload: { snapshot: selectedSnapshot, loader },
      });
    }
    const inventoryBody = {
      columns: ['id'].concat(
        inventoryColumns.filter((column) => column !== 'select'),
      ),
      snapshot: selectedSnapshot,
      filters: {},
      pagination: {
        start: 0,
        limit: 20,
      },
    };
    const devices = yield call(requestForSaga, {
      url: '/tables/inventory/devices',
      method: 'POST',
      data: inventoryBody,
    });
    yield put({
      type: `${SAVE_DEVICES}_SUCCESS`,
      payload: {
        devices: devices.data,
        snapshot: selectedSnapshot,
      },
    });
  } catch (e) {
    yield put({
      type: `${SAVE_DEVICES}_ERROR`,
      payload: { snapshot: selectedSnapshot },
    });
    yield call(requestCustomErrorMessage, 'danger', e);
  }
}

function* jobAddedSaga(action) {
  const payload = get(action, 'payload', []);
  const uploadScheduled = payload.filter(
    (job) =>
      job.status === JOB_STATUSES.scheduled &&
      job.action === JOB_ACTIONS.snapshotLoad &&
      get(job, 'args.upload') === true,
  );
  if (uploadScheduled.length) {
    const jobs = yield getJobs();
    if (jobs.size) {
      yield put({ type: `${UPLOAD_SNAPSHOT}_STOP` });
    }
  }
  const discoveryScheduled = payload.some(
    (job) =>
      job.status === JOB_STATUSES.scheduled &&
      job.action === JOB_ACTIONS.discovery &&
      job.cron === false,
  );
  if (discoveryScheduled) {
    yield call(
      displayMessage,
      'info',
      'snapshotManagement.request.discoveryScheduled',
    );
  }
}

function* jobStartedSaga(action) {
  const payload = get(action, 'payload', []);
  const loadingRun = payload.find(
    (job) =>
      job.status === JOB_STATUSES.running &&
      job.action === JOB_ACTIONS.snapshotLoad,
  );
  if (loadingRun) {
    const snapshots = yield getSnapshots();
    const selectedSnapshot = yield getSelectedSnapshot();
    const loadingSnapshotId = get(loadingRun, ['snapshot']);
    yield put({
      type: LOADING_STARTED,
      payload: { id: loadingSnapshotId },
    });
    const foundSnapshot = snapshots.find(
      (snapshot) => snapshot.get('id') === loadingSnapshotId,
    );
    if (
      (!selectedSnapshot ||
        !foundSnapshot ||
        (foundSnapshot &&
          foundSnapshot.get('status') !== JOB_STATUSES.running)) &&
      userInSnapshotsManagement()
    ) {
      if (window.location.pathname === `${PAGE_URL}/${loadingSnapshotId}`) {
        yield put(changeSnapshot(loadingSnapshotId));
      } else if (get(loadingRun, 'args.upload', false)) {
        yield call(fetchSnapshotsSaga);
      }
    }
  }
  const refreshStarted = payload.find(
    (job) =>
      job.action === JOB_ACTIONS.discovery &&
      job.status === JOB_STATUSES.running &&
      get(job, 'args.action') === 'refresh',
  );
  if (refreshStarted) {
    yield call(fetchDevices, get(refreshStarted, ['args', 'snList']));
    yield put(
      showRefreshLoader({
        value: false,
        snapshotId: get(refreshStarted, 'snapshot'),
      }),
    );
  }
  const addStarted = payload.find(
    (job) =>
      job.action === JOB_ACTIONS.discovery &&
      job.status === JOB_STATUSES.running &&
      get(job, 'args.action') === 'add',
  );
  if (addStarted) {
    yield call(fetchDevices);
    yield put(
      showAddLoader({ value: false, snapshotId: get(addStarted, 'snapshot') }),
    );
  }
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy code
function* jobUpdatedSaga(action) {
  const payload = get(action, 'payload', []);
  if (Array.isArray(payload)) {
    const selectedSnapshot = yield getSelectedSnapshot();
    const deleteDeviceStop = payload.find(
      (job) =>
        (job.status === JOB_STATUSES.error ||
          job.status === JOB_STATUSES.cancelled ||
          job.status === JOB_STATUSES.stopped) &&
        job.action === JOB_ACTIONS.deleteDevice,
    );
    if (deleteDeviceStop) {
      yield put({
        type: DELETE_DEVICE_STOP,
        payload: {
          snapshotId: get(deleteDeviceStop, ['snapshot']),
        },
      });
    }
    const errorJobs = payload.some(
      (job) => job.status === JOB_STATUSES.error && job.read === false,
    );
    if (errorJobs) {
      yield put(enableStartBtn());
      yield call(fetchSnapshotsSaga);
      return;
    }
    const recalculateSitesDone = payload.find(
      (job) =>
        (job.status === JOB_STATUSES.done ||
          job.status === JOB_STATUSES.stopped) &&
        job.action === JOB_ACTIONS.recalculateSites,
    );
    if (recalculateSitesDone) {
      yield fetchSnapshotsSaga();
    }
    const snapshotLoadCancelled = payload.find(
      (job) =>
        job.status === JOB_STATUSES.cancelled &&
        job.action === JOB_ACTIONS.snapshotLoad &&
        job.done,
    );
    if (snapshotLoadCancelled) {
      yield fetchSnapshotsSaga();
    }
    const topologyCalculationStopped = payload.find(
      (job) =>
        job.status === JOB_STATUSES.stopped &&
        job.action === JOB_ACTIONS.topologyCalculation &&
        job.done,
    );
    if (topologyCalculationStopped) {
      yield fetchSnapshotsSaga();
      yield call(
        displayMessage,
        'info',
        'snapshotManagement.request.topologyCalculationStopped',
      );
    }
    // A manually-triggered topology calculation on a canceled snapshot leaves it
    // canceled (so the button stays visible), whether it finished or errored.
    // Refetch so the recomputed `topologyCalculationAlreadyRun` flag disables the
    // button - it may only run once per load, and the backend rejects a retry
    // once any topology calculation exists for this load. (The cancel/stop case
    // is handled by the `topologyCalculationStopped` branch above.)
    const manualTopologyCalculationFinished = payload.find(
      (job) =>
        (job.status === JOB_STATUSES.done ||
          job.status === JOB_STATUSES.error) &&
        job.action === JOB_ACTIONS.topologyCalculation &&
        get(job, 'args.action') === 'manual',
    );
    if (manualTopologyCalculationFinished) {
      yield fetchSnapshotsSaga();
    }
    // devices deleted
    const deleteDeviceDone = payload.find(
      (job) =>
        job.status === JOB_STATUSES.done &&
        job.action === JOB_ACTIONS.deleteDevice,
    );
    if (deleteDeviceDone) {
      const snapshotId = get(deleteDeviceDone, ['snapshot']);
      yield put({
        type: DELETE_DEVICE_FINISH,
        payload: {
          snapshotId,
          devices: get(deleteDeviceDone, ['args', 'snList']),
        },
      });
      yield fetchSnapshotDataSaga(snapshotId);
    }
    const discoveryFinished = payload.some(
      (job) =>
        job.action === JOB_ACTIONS.discovery &&
        (job.status === JOB_STATUSES.done ||
          job.status === JOB_STATUSES.stopped),
    );
    if (discoveryFinished) {
      const snapshotId = get(discoveryFinished, ['snapshot']);
      yield fetchSnapshotsSaga();
      if (isSnapshotManagementPage(snapshotId)) {
        yield put({ type: CLEAR_DEVICES });
      }
    }
    const reportsDone = payload.filter(
      (job) =>
        job.action === JOB_ACTIONS.report &&
        job.status === JOB_STATUSES.done &&
        (get(job, 'args.action') === JOB_ACTIONS.discovery ||
          get(job, 'args.action') === JOB_ACTIONS.snapshotLoad),
    );
    if (reportsDone.length) {
      yield all(
        reportsDone.map((job) =>
          call(
            displayMessage,
            'success',
            `snapshotManagement.request.${get(job, 'args.action')}Finished`,
          ),
        ),
      );
    }

    const refreshStarted = payload.find(
      (job) =>
        job.action === JOB_ACTIONS.discovery &&
        (job.status === JOB_STATUSES.running ||
          job.status === JOB_STATUSES.cancelled) &&
        get(job, 'args.action') === 'refresh',
    );
    if (refreshStarted) {
      const snapshotId = get(refreshStarted, ['snapshot']);
      if (
        isSnapshotManagementPage(snapshotId) &&
        // skip if no snapshot is selected. If this happens, socket message arrived before page was loaded
        // this is safe, devices will be fetched on snapshot management page mount
        selectedSnapshot !== null
      ) {
        yield call(fetchDevices, get(refreshStarted, ['args', 'snList']));
        yield put(
          showRefreshLoader({
            value: false,
            snapshotId: get(refreshStarted, ['args', 'vCollectKey']),
          }),
        );
      }
    }
    const addStarted = payload.find(
      (job) =>
        job.action === JOB_ACTIONS.discovery &&
        (job.status === JOB_STATUSES.running ||
          job.status === JOB_STATUSES.cancelled) &&
        get(job, 'args.action') === 'add',
    );
    if (addStarted) {
      const snapshotId = get(addStarted, ['snapshot']);
      if (
        isSnapshotManagementPage(snapshotId) &&
        // skip if no snapshot is selected. If this happens, socket message arrived before page was loaded
        // this is safe, devices will be fetched on snapshot management page mount
        selectedSnapshot !== null
      ) {
        yield call(fetchDevices);
        yield put(
          showAddLoader({
            value: false,
            snapshotId: get(addStarted, ['args', 'vCollectKey']),
          }),
        );
      }
    }
    const deleteCancelled = payload.some(
      (job) =>
        job.status === JOB_STATUSES.cancelled &&
        job.action === JOB_ACTIONS.deleteDevice,
    );
    if (deleteCancelled) {
      yield put({ type: `${DELETE_DEVICE}_CANCEL` });
    }
    const removedJobs = payload.filter(
      (job) => job.status === JOB_STATUSES.done,
    );
    if (removedJobs.length) {
      const findAction = (jobAction) =>
        removedJobs.filter((job) => job.action === jobAction);
      const loadedJobs = findAction(JOB_ACTIONS.snapshotLoad);
      if (loadedJobs.length) {
        yield put(enableStartBtn());
        yield all(
          loadedJobs.map((job) =>
            put({ type: LOAD_SNAPSHOT_FINISH, payload: { id: job.id } }),
          ),
        );
      }
      const unloadedJobs = findAction(JOB_ACTIONS.snapshotUnload);
      if (unloadedJobs.length) {
        yield all(
          unloadedJobs.map((job) =>
            put({ type: UNLOAD_SNAPSHOT_FINISH, payload: { id: job.id } }),
          ),
        );
      }
      const deletedJobs = findAction(JOB_ACTIONS.snapshotDelete);
      if (deletedJobs.length) {
        yield all(
          deletedJobs.map((job) =>
            put({ type: DELETE_SNAPSHOT_FINISH, payload: { id: job.id } }),
          ),
        );
      }
      if (loadedJobs.length || unloadedJobs.length || deletedJobs.length) {
        return;
      }
    }
    const siteRecalculateStarted = payload.some(
      (job) =>
        job.action === JOB_ACTIONS.recalculateSites &&
        job.status === JOB_STATUSES.running,
    );
    if (siteRecalculateStarted) {
      yield call(
        displayMessage,
        'info',
        'snapshotManagement.request.siteRecalculationStarted',
      );
    }
    const siteRecalculateDone = payload.some(
      (job) =>
        job.action === JOB_ACTIONS.recalculateSites &&
        job.status === JOB_STATUSES.done,
    );
    if (siteRecalculateDone) {
      yield call(
        displayMessage,
        'success',
        'snapshotManagement.request.siteRecalculationFinished',
      );
    }
  }
  if (
    Array.isArray(payload) &&
    payload.some(
      (item) =>
        item.action === JOB_ACTIONS.snapshotClone &&
        item.status === JOB_STATUSES.done,
    )
  ) {
    yield call(displayMessage, 'success', 'snapshotManagement.request.cloned');
    yield fetchSnapshotsSaga();
  }
}

export function* cloneSnapshotSaga(action) {
  const { id, name, tsStart } = action.payload;
  try {
    yield put({ type: `${CLONE_SNAPSHOT}_START`, payload: { id } });
    yield call(requestForSaga, {
      url: `/snapshots/${id}/clone`,
      method: 'POST',
      data: { jobDetail: name || tsStart },
    });
  } catch (e) {
    yield put({ type: `${CLONE_SNAPSHOT}_ERROR`, payload: { id } });
    if (['API_SNAPSHOT_INVALID_FORMAT'].includes(get(e, 'code'))) {
      yield call(requestCustomErrorMessage, 'warning', e);
      return;
    }
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* setSharperSettingsSaga(action) {
  try {
    const payload = yield call(requestForSaga, {
      url: '/discovery/shaper',
      method: 'POST',
      data: { bandwidth: action.payload.bandwidth },
    });
    yield put({
      type: `${SET_SHAPER_SETTINGS}_SUCCESS`,
      payload,
    });
    const language = yield select(selectLanguage);
    const messageKey =
      payload.bandwidth === -1
        ? 'bandwidthHasBeenDisabled'
        : 'bandwidthHasBeenSet';

    const message =
      translationMessages[language.get('locale')].request[messageKey];
    yield put(
      showSuccess({
        message: message.replace('{bandwidth}', payload.bandwidth),
      }),
    );
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: hic sunt dracones
function* discoveryProcessStartSaga(action) {
  const {
    callback,
    onError,
    redirect = false,
    requestData = {},
  } = action.payload;
  const requestAction = get(requestData, 'action');
  try {
    yield put({ type: `${DISCOVERY_PROCESS_START}_START` });
    const selectedSnapshot = yield getSelectedSnapshot();
    if (!requestAction || requestAction === 'new') {
      // Settings are now saved separately via the new DS form (PATCH /snapshots/:key/settings) before discovery starts.
      // This POST intentionally sends no settings payload.
      const data = null;
      const jobs = yield getJobs();
      const isAnyRunning = jobs.some(
        (job) => job.get('status') === JOB_STATUSES.running,
      );
      if (!isAnyRunning) {
        yield put(disableStartBtn());
      }
      yield call(requestForSaga, {
        url: '/snapshots',
        data,
        method: 'POST',
      });
    }
    if (requestAction === 'refresh') {
      yield call(requestForSaga, {
        url: `/snapshots/${selectedSnapshot}/devices`,
        data: pipe(
          (data) =>
            requestData.credentials
              ? assoc('credentials', requestData.credentials, data)
              : data,
          (data) =>
            requestData.privileges
              ? assoc('privileges', requestData.privileges, data)
              : data,
        )({ snList: get(requestData, 'snList', []) }),
        method: 'POST',
      });
    }
    if (requestAction === 'add') {
      yield call(requestForSaga, {
        url: `/snapshots/${selectedSnapshot}/devices`,
        data: pipe(
          (data) =>
            requestData.credentials
              ? assoc('credentials', requestData.credentials, data)
              : data,
          (data) =>
            requestData.privileges
              ? assoc('privileges', requestData.privileges, data)
              : data,
        )({
          ipList: get(requestData, 'ipList', []),
          retryTimedOut: get(requestData, 'retryTimedOut'),
          vendorApi: [],
        }),
        method: 'POST',
      });
    }
    if (redirect) {
      yield put(push(`/snapshot-management/${requestData.vCollectKey}`));
    }
    yield put({
      type: `${DISCOVERY_PROCESS_START}_SUCCESS`,
      payload: { requestAction },
    });
    if (typeof callback === 'function') {
      callback();
    }
  } catch (e) {
    if (onError) {
      onError();
    }
    yield put(enableStartBtn());
    yield put({ type: `${DISCOVERY_PROCESS_START}_ERROR` });

    const vendorApiHasError = get(e, 'payload.response.data.isVendorApiError');
    if (vendorApiHasError) {
      yield call(requestCustomErrorMessage, 'danger', e);
      return;
    }

    if (get(e, 'code') === 'API_EMPTY_CREDENTIALS') {
      yield call(
        displayMessage,
        'warning',
        'snapshotManagement.request.setCredentialsFirst',
      );
      return;
    }
    if (get(e, 'code') === 'API_DISCOVERY_STARTING_ALREADY') {
      yield call(
        displayMessage,
        'success',
        'snapshotManagement.request.alreadyStarting',
      );
      return;
    }
    if (get(e, 'code') === 'API_NOTHING_TO_DO') {
      yield call(
        displayMessage,
        'warning',
        'snapshotManagement.request.nothingToDo',
      );
      return;
    }
    if (get(e, 'code') === 'API_EMPTY_CREDENTIALS') {
      yield call(
        displayMessage,
        'warning',
        'snapshotManagement.request.noAuthentication',
      );
      return;
    }
    if (get(e, 'code') === 'API_SNAPSHOT_LOCKED') {
      yield call(requestCustomErrorMessage, 'warning', e);
      return;
    }
    if (get(e, 'code') === 'API_INVALID_IP_LIST') {
      yield call(requestCustomErrorMessage, 'danger', e);
      return;
    }
    if (
      get(e, 'code') === 'API_INVALID_RESOURCE' &&
      get(e, 'errors', []).some(
        (error) => error.message === 'MULTIPLE_ROUTING_SWITCHING_RULES',
      )
    ) {
      yield call(
        displayMessage,
        'warning',
        'settingsSiteSeparation.request.multipleRouting',
      );
      return;
    }
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* snapshotStatusReceivedSaga(action) {
  try {
    const status = get(action, ['payload', 'vCollect', 'status']);
    const selectedSnapshot = yield getSelectedSnapshot();
    if (selectedSnapshot === get(action, 'payload.vCollect._key')) {
      yield put({
        type: `${SNAPSHOT_STATUS_RECEIVED}_SUCCESS`,
        payload: action.payload,
      });

      switch (status) {
        case 'done': {
          const licenseLimit = get(action, [
            'payload',
            'vCollect',
            'licenseLimit',
          ]);
          const licensedDevCount = get(action, [
            'payload',
            'vCollect',
            'licensedDevCount',
          ]);
          if (
            !new LicenseLimitChecker({ devices: licenseLimit }).lteDeviceLimit({
              devices: licensedDevCount,
            })
          ) {
            yield call(
              displayMessage,
              'warning',
              'snapshotManagement.request.discoveryLicenseExceeded',
            );
          }
          break;
        }
      }
    }
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* downloadLogSaga(action) {
  try {
    const selectedSnapshot = yield getSelectedSnapshot();
    const log = yield call(requestForSaga, {
      url: `/os/service-log/${action.payload.downloadLogKey}/${selectedSnapshot}?size=all`,
      method: 'GET',
    });
    const blob = new Blob([log], {
      type: 'text/plain;charset=utf-8',
    });
    // !! DO NOT REUSE
    // Downloads should be handled by providing proper headers from backend and initiated using `<a href=""`
    // to use browser's native progress indication and avoid issues with large files.
    FileSaver.saveAs(blob, `${action.payload.downloadLogKey}.txt`); // !! DO NOT REUSE
  } catch (e) {
    if (get(e, 'code') === 'API_NOT_FOUND') {
      yield call(
        displayMessage,
        'warning',
        'snapshotManagement.request.logNotFound',
      );
    } else {
      yield call(requestCustomErrorMessage, 'danger', e);
      throw e;
    }
  } finally {
    yield put({
      type: `${action.type}_FINISH`,
      payload: action.payload,
    });
  }
}

function* newDeviceSavedSaga(action) {
  try {
    const { _ci } = action.payload;
    // _ci can be in the format of "vCollect/<uuid>" (Arango) or just "<uuid>" (PSQL).
    const snapshotId = _ci.includes('/') ? _ci.split('/')[1] : _ci;
    const selectedSnapshot = yield getSelectedSnapshot();
    if (selectedSnapshot === snapshotId) {
      yield put({
        type: `${NEW_DEVICE_SAVED}_SUCCESS`,
        payload: action.payload,
      });
    }
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* logsReceivedSaga(action) {
  try {
    const selectedSnapshot = yield getSelectedSnapshot();
    const selectedSnapshotMessages = action.payload.filter(
      (message) => get(message, 'vCollect._key') === selectedSnapshot,
    );
    if (selectedSnapshotMessages.length) {
      yield put({
        type: `${action.type}_SUCCESS`,
        payload: selectedSnapshotMessages,
      });
    }
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* snapshotUpdateReceivedSaga(action) {
  try {
    const selectedSnapshot = yield getSelectedSnapshot();
    if (selectedSnapshot === get(action, 'payload.vCollect._key')) {
      yield put({
        type: `${action.type}_SUCCESS`,
        payload: action.payload,
      });
    }
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* bandwidthMsgReceivedSaga(action) {
  const snapshots = yield getSnapshots();
  if (isSnapshotRunning(snapshots)) {
    yield put({
      type: `${BANDWIDTH_MSG_RECEIVED}_SUCCESS`,
      payload: action.payload,
    });
  }
}

function* workerLastCommunicationSaga(action) {
  const snapshots = yield getSnapshots();
  if (isSnapshotRunning(snapshots)) {
    yield put({
      type: `${WORKER_LAST_COMMUNICATION}_SUCCESS`,
      payload: action.payload,
    });
  }
}

function* serverTsReceivedSaga(action) {
  const snapshots = yield getSnapshots();
  const runningSnapshot = isSnapshotRunning(snapshots);
  if (runningSnapshot) {
    yield put({
      type: `${SERVER_TS_RECEIVED}_SUCCESS`,
      payload: assoc('id', runningSnapshot.get('id'), action.payload),
    });
  }
}

function* fetchDiscoveryInitSaga() {
  try {
    yield put({ type: 'FETCH_START' });
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  } finally {
    yield put({ type: 'FETCH_SUCCESS' });
  }
}

function* deleteDeviceSaga(action) {
  try {
    const { devices, snapshotId } = action.payload;
    const snapshots = yield getSnapshots();
    const lockedSnapshot = snapshots.some(
      (snapshot) => snapshot.get('id') === snapshotId && snapshot.get('locked'),
    );
    if (!lockedSnapshot) {
      yield put({
        type: `${DELETE_DEVICE}_START`,
        payload: { devices, snapshotId, lockedSnapshot },
      });
    }
    yield call(requestForSaga, {
      url: `/snapshots/${snapshotId}/devices`,
      method: 'DELETE',
      data: devices,
    });
  } catch (e) {
    yield put({
      type: `${DELETE_DEVICE}_ERROR`,
      payload: { snapshotId: action.payload.snapshotId },
    });
    if (get(e, 'code') === 'API_SNAPSHOT_LOCKED') {
      yield call(requestCustomErrorMessage, 'warning', e);
      return;
    }
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* openNewSettingsSaga(action) {
  try {
    yield put({
      type: `${OPEN_NEW_SETTINGS}_START`,
    });
    const [settings] = yield all([
      call(requestForSaga, { url: '/settings', method: 'GET' }),
      call(fetchIpListSaga),
      call(fetchPwdList),
      call(fetchEnablePwdList),
    ]);
    const {
      discoveryTasks,
      disabledPostDiscoveryActions,
      siteSeparation,
      discoveryHistorySeeds,
    } = settings;
    yield put({
      type: `${FETCH_SITE_SEPARATION}_SUCCESS`,
      payload: {
        siteSeparation,
      },
    });
    yield put({
      type: `${FETCH_DISCOVERY_TASKS}_SUCCESS`,
      payload: {
        discoveryTasks,
      },
    });
    yield put({
      type: `${FETCH_DISABLED_POST_DISCOVERY_ACTIONS}_SUCCESS`,
      payload: {
        disabledPostDiscoveryActions,
      },
    });
    yield put({
      type: `${FETCH_HISTORY_SEEDS}_SUCCESS`,
      payload: {
        data: discoveryHistorySeeds,
      },
    });
    yield put({
      type: `${OPEN_NEW_SETTINGS}_SUCCESS`,
    });
    const disableRedirect = get(action, 'payload.disableRedirect', false);
    if (!disableRedirect) {
      yield put(
        push({
          pathname: `${PAGE_URL}/new/settings`,
          hash: get(action, 'payload.location.hash', ''),
        }),
      );
    }
  } catch (e) {
    yield put({
      type: `${OPEN_NEW_SETTINGS}_ERROR`,
    });
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* openSettingsSaga(action) {
  try {
    yield put({
      type: `${OPEN_SETTINGS}_START`,
    });
    const selectedSnapshot = yield getSelectedSnapshot();
    const [settings] = yield all([
      call(requestForSaga, {
        url: `/snapshots/${selectedSnapshot}/settings`,
        method: 'GET',
      }),
      call(fetchPwdList, FETCH_GLOBAL_PWDLIST),
      call(fetchEnablePwdList, FETCH_GLOBAL_ENABLE_PWDLIST),
    ]);
    const {
      credentials,
      disabledPostDiscoveryActions,
      discoveryTasks,
      privileges,
      seedList,
      siteSeparation,
      discoveryHistorySeeds,
    } = settings;
    const correctCredentials = credentials.filter(
      (item) =>
        typeof item.password === 'string' ||
        typeof item.privateKey === 'string',
    );
    const filteredCredentialsLength =
      credentials.length - correctCredentials.length;
    const correctPrivileges = privileges.filter(
      (item) => typeof item.password === 'string',
    );
    const filteredPrivilegesLength =
      privileges.length - correctPrivileges.length;
    if (
      !get(action, 'payload.skipPasswordOverlay', false) &&
      (filteredCredentialsLength || filteredPrivilegesLength)
    ) {
      yield put(
        saveInvalidPasswords({
          credentials: filteredCredentialsLength,
          privileges: filteredPrivilegesLength,
        }),
      );
      yield put(toggleConfirmDialog(SETTINGS_NO_PASSWORD_DIALOGUE_NAME));
      yield take(CLOSE_SETTINGS_NO_PASSWORDS);
    }
    yield put({
      type: `${FETCH_SITE_SEPARATION}_SUCCESS`,
      payload: {
        siteSeparation,
      },
    });
    yield put({
      type: `${FETCH_DISCOVERY_TASKS}_SUCCESS`,
      payload: {
        discoveryTasks,
      },
    });
    yield put({
      type: `${FETCH_DISABLED_POST_DISCOVERY_ACTIONS}_SUCCESS`,
      payload: {
        disabledPostDiscoveryActions,
      },
    });
    yield put({
      type: `${FETCH_IPLIST}_SUCCESS`,
      payload: {
        ipList: seedList,
      },
    });

    yield put({
      type: `${FETCH_HISTORY_SEEDS}_SUCCESS`,
      payload: {
        data: discoveryHistorySeeds,
      },
    });

    yield put({
      type: `${LOAD_PWDLIST}_SUCCESS`,
      payload: {
        datalist: { data: correctCredentials },
        loadData: true,
      },
    });

    yield put({
      type: `${LOAD_ENABLE_PWD}_SUCCESS`,
      payload: {
        datalist: { data: correctPrivileges },
        loadData: true,
      },
    });
    yield put({
      type: `${OPEN_SETTINGS}_SUCCESS`,
    });
    const disableRedirect = get(action, 'payload.disableRedirect', false);
    if (!disableRedirect) {
      yield put(
        push({
          pathname: `${PAGE_URL}/${selectedSnapshot}/settings`,
          hash: get(action, 'payload.location.hash', ''),
        }),
      );
    }
  } catch (e) {
    yield put({
      type: `${OPEN_SETTINGS}_ERROR`,
    });
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function storeSnapshotToLocalStorage(action) {
  const value = get(action, 'payload.value', null);
  localStorage.setItem(SNAPSHOT_ID_LS_KEY, value);
}

export function* defaultSaga() {
  yield all([
    takeEvery(JOB_UPDATED, jobUpdatedSaga),
    takeEvery(JOB_STARTED, jobStartedSaga),
    takeEvery(JOB_QUEUED, jobUpdatedSaga),
    takeEvery(JOB_ADDED, jobAddedSaga),
    takeEvery(JOB_STARTED, jobStartedSaga),
    takeEvery(CLONE_SNAPSHOT, cloneSnapshotSaga),
    takeEvery(DELETE_SNAPSHOT_FINISH, realodDataSaga),
    takeEvery(DELETE_SNAPSHOT, deleteSnapshotsSaga),
    takeEvery(DOWNLOAD_SNAPSHOT, downloadSnapshotSaga),
    takeEvery(RUN_TOPOLOGY_CALCULATION, runTopologyCalculationSaga),
    takeEvery(FETCH_SNAPSHOT_MANAGEMENT, fetchSnapshotsSaga),
    takeEvery(FETCH_SNAPSHOT_DATA, fetchSnapshotDataSaga),
    takeEvery(LOAD_SNAPSHOT_FINISH, realodDataSaga),
    takeEvery(LOAD_SNAPSHOT, loadSnapshotsSaga),
    takeEvery(LOCK_SNAPSHOT, lockSnapshotSaga),
    takeEvery(NAME_CHANGE, nameChangeSaga),
    takeEvery(NOTE_CHANGE, noteChangeSaga),
    takeEvery(UNLOAD_SNAPSHOT_FINISH, realodDataSaga),
    takeEvery(UNLOAD_SNAPSHOT, unloadSnapshotsSaga),
    takeEvery(UNLOCK_SNAPSHOT, unlockSnapshotSaga),
    takeEvery(UPLOAD_SNAPSHOT_FINISH, realodDataSaga),
    takeEvery(UPLOAD_SNAPSHOT, uploadSnapshotSaga),
    takeEvery(DELETE_DEVICE, deleteDeviceSaga),
    takeEvery(FETCH_DEVICES, fetchDevices),
    takeEvery(OPEN_NEW_SETTINGS, openNewSettingsSaga),
    takeEvery(OPEN_SETTINGS, openSettingsSaga),
    takeEvery(FETCH_LOGS, fetchLogsSaga),
    takeEvery(SET_SHAPER_SETTINGS, setSharperSettingsSaga),
    takeEvery(FETCH_DISCOVERY_INIT, fetchDiscoveryInitSaga),
    takeEvery(DISCOVERY_PROCESS_START, discoveryProcessStartSaga),
    takeEvery(DISCOVERY_PROCESS_STOP, jobStopSaga),
    takeEvery(SNAPSHOT_STATUS_RECEIVED, snapshotStatusReceivedSaga),
    takeEvery(DOWNLOAD_LOG, downloadLogSaga),
    takeEvery(NEW_DEVICE_SAVED, newDeviceSavedSaga),
    takeEvery(TASKER_LOGS_RECEIVED, logsReceivedSaga),
    takeEvery(WORKER_LOGS_RECEIVED, logsReceivedSaga),
    takeEvery(NETWORKER_LOGS_RECEIVED, logsReceivedSaga),
    takeEvery(SNAPSHOT_INFO_RECEIVED, snapshotUpdateReceivedSaga),
    takeEvery(BANDWIDTH_MSG_RECEIVED, bandwidthMsgReceivedSaga),
    takeEvery(WORKER_LAST_COMMUNICATION, workerLastCommunicationSaga),
    takeEvery(SERVER_TS_RECEIVED, serverTsReceivedSaga),
    takeEvery(CHANGE_SNAPSHOT, storeSnapshotToLocalStorage),
  ]);
}

// All sagas to be loaded
export default defaultSaga;
